import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../lib/api';
import type {
  ChecklistResponse,
  EvidenceResponse,
  Project,
  ProjectSummary,
  ReportBranding,
} from '../types';

type BuilderStep = 'machine' | 'documents' | 'checklist' | 'export';

const STEPS: Array<{ id: BuilderStep; label: string; description: string }> = [
  {
    id: 'machine',
    label: 'Machine information',
    description: 'Describe the machine and its intended use.',
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'Review the technical-file evidence collected so far.',
  },
  {
    id: 'checklist',
    label: 'Checklist review',
    description: 'Resolve missing evidence and review compliance gaps.',
  },
  {
    id: 'export',
    label: 'Export',
    description: 'Download the project report when it is ready for review.',
  },
];

export default function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [evidenceCount, setEvidenceCount] = useState(0);
  const [itemsNeedingAttention, setItemsNeedingAttention] = useState(0);
  const [step, setStep] = useState<BuilderStep>('machine');
  const [name, setName] = useState('');
  const [productType, setProductType] = useState('');
  const [intendedUse, setIntendedUse] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRequestingReview, setIsRequestingReview] = useState(false);
  const [branding, setBranding] = useState<ReportBranding | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let canceled = false;
    Promise.all([
      api.get<Project>(`/api/projects/${projectId}`),
      api.get<ProjectSummary>(`/api/projects/${projectId}/summary`),
      api.get<EvidenceResponse>(`/api/projects/${projectId}/evidence`),
      api.get<ChecklistResponse>(`/api/projects/${projectId}/checklist`),
      api.get<ReportBranding>(`/api/projects/${projectId}/report-branding`),
    ])
      .then(
        ([
          projectResponse,
          summaryResponse,
          evidenceResponse,
          checklistResponse,
          brandingResponse,
        ]) => {
          if (canceled) return;
          const loadedProject = projectResponse.data;
          setProject(loadedProject);
          setSummary(summaryResponse.data);
          setEvidenceCount(evidenceResponse.data.total);
          setItemsNeedingAttention(
            checklistResponse.data.checklistItems.filter(item =>
              ['missing', 'not_started', 'needs_review'].includes(item.status)
            ).length
          );
          setName(loadedProject.name);
          setProductType(loadedProject.productType ?? '');
          setIntendedUse(loadedProject.intendedUse ?? '');
          setBranding(brandingResponse.data);
        }
      )
      .catch(() => {
        if (!canceled) setError('This project could not be opened.');
      });
    return () => {
      canceled = true;
    };
  }, [projectId]);

  const resultsPath = summary?.latestScanId
    ? `/scan/${summary.latestScanId}/results`
    : '/scan';
  const canEditProject = project
    ? project.currentUserRole !== 'read_only'
    : false;
  const canConfigureProject = project
    ? ['owner', 'admin'].includes(project.currentUserRole)
    : false;
  const checklistPercent = useMemo(() => {
    if (!summary?.checklistTotal) return 0;
    return Math.round(
      (summary.checklistCompleted / summary.checklistTotal) * 100
    );
  }, [summary]);

  const saveMachineDetails = async (event: FormEvent) => {
    event.preventDefault();
    if (!projectId || !name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const response = await api.patch<Project>(
        `/api/projects/${projectId}/details`,
        {
          name: name.trim(),
          productType: productType.trim(),
          intendedUse: intendedUse.trim(),
        }
      );
      setProject(response.data);
      setStep('documents');
    } catch {
      setError('Could not save the machine information.');
    } finally {
      setIsSaving(false);
    }
  };

  const requestReview = async () => {
    if (!projectId) return;
    setIsRequestingReview(true);
    setError(null);
    try {
      const response = await api.post<Project>(
        `/api/projects/${projectId}/review-transition`,
        { action: 'request_review' }
      );
      setProject(response.data);
    } catch {
      setError('Could not request consultant review.');
    } finally {
      setIsRequestingReview(false);
    }
  };

  const saveBranding = async () => {
    if (!projectId || !branding) return;
    setIsSaving(true);
    setError(null);
    try {
      const response = await api.put<ReportBranding>(
        `/api/projects/${projectId}/report-branding`,
        branding
      );
      setBranding(response.data);
    } catch {
      setError('Could not save report branding.');
    } finally {
      setIsSaving(false);
    }
  };

  if (error && !project) {
    return (
      <section className="route-error-card animate-fade-in">
        <span className="route-error-eyebrow">Builder mode</span>
        <h1 className="route-error-title">Project unavailable</h1>
        <p className="route-error-message">{error}</p>
        <Link className="button button-primary" to="/history">
          Back to projects
        </Link>
      </section>
    );
  }

  if (!project || !summary) {
    return (
      <div style={{ padding: 80, color: 'var(--text-muted)' }}>
        Loading project workspace…
      </div>
    );
  }

  return (
    <div
      className="animate-fade-in"
      style={{ maxWidth: 960, margin: '0 auto' }}
    >
      <div className="page-intro">
        <div className="page-eyebrow">BUILDER MODE</div>
        <h1 style={{ fontFamily: 'var(--font-display)', margin: '6px 0' }}>
          {project.name}
        </h1>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
          A guided path to assemble your machinery compliance file in plain
          language.
        </p>
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}
        >
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            Review status: {project.reviewStatus.replace(/_/g, ' ')}
          </span>
          {['owner', 'admin', 'reviewer'].includes(project.currentUserRole) && (
            <Link
              className="button button-secondary"
              to={`/projects/${project.id}/consultant`}
            >
              Open consultant mode
            </Link>
          )}
          {['owner', 'contributor'].includes(project.currentUserRole) &&
            ['draft', 'changes_requested'].includes(project.reviewStatus) && (
              <button
                className="button button-primary"
                type="button"
                disabled={isRequestingReview}
                onClick={() => void requestReview()}
              >
                {isRequestingReview
                  ? 'Requesting review…'
                  : 'Request consultant review'}
              </button>
            )}
        </div>
        {project.reviewStatus === 'review_requested' && canEditProject && (
          <p
            role="status"
            style={{ color: 'var(--warning, #b7791f)', marginBottom: 0 }}
          >
            A consultant review is pending. Changes to project details,
            evidence, or checklist items may invalidate that review.
          </p>
        )}
      </div>

      <nav
        aria-label="Builder steps"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 8,
          margin: '24px 0',
        }}
      >
        {STEPS.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setStep(item.id)}
            className="button button-secondary"
            style={{
              minHeight: 72,
              textAlign: 'left',
              opacity: step === item.id ? 1 : 0.72,
              borderColor:
                step === item.id ? 'var(--accent-primary)' : undefined,
            }}
          >
            <span
              style={{
                display: 'block',
                fontSize: 11,
                color: 'var(--text-muted)',
              }}
            >
              STEP {index + 1}
            </span>
            {item.label}
          </button>
        ))}
      </nav>

      <section
        className="results-panel"
        style={{ padding: '28px', marginBottom: 24 }}
      >
        <h2 style={{ fontFamily: 'var(--font-display)', marginTop: 0 }}>
          {STEPS.find(item => item.id === step)?.label}
        </h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: -8 }}>
          {STEPS.find(item => item.id === step)?.description}
        </p>
        {error && (
          <p role="alert" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}

        {step === 'machine' && (
          <form
            onSubmit={saveMachineDetails}
            style={{ display: 'grid', gap: 16 }}
          >
            <label style={{ display: 'grid', gap: 6 }}>
              <span>Machine name</span>
              <input
                value={name}
                maxLength={200}
                required
                disabled={!canEditProject}
                onChange={event => setName(event.target.value)}
              />
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span>Machine or product type</span>
              <input
                value={productType}
                maxLength={200}
                disabled={!canEditProject}
                placeholder="For example: automated packaging line"
                onChange={event => setProductType(event.target.value)}
              />
            </label>
            <label style={{ display: 'grid', gap: 6 }}>
              <span>Intended use</span>
              <textarea
                value={intendedUse}
                maxLength={2000}
                rows={5}
                disabled={!canEditProject}
                placeholder="What is the machine designed to do, where will it be used, and who operates it?"
                onChange={event => setIntendedUse(event.target.value)}
              />
            </label>
            <button
              className="button button-primary"
              type="submit"
              disabled={isSaving || !name.trim() || !canEditProject}
              style={{ justifySelf: 'start' }}
            >
              {isSaving ? 'Saving…' : 'Save and continue'}
            </button>
          </form>
        )}

        {step === 'documents' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              You have {evidenceCount} document{evidenceCount === 1 ? '' : 's'}{' '}
              in this project.{' '}
              {itemsNeedingAttention > 0
                ? `${itemsNeedingAttention} checklist item${itemsNeedingAttention === 1 ? ' needs' : 's need'} supporting evidence or review.`
                : 'The checklist has no evidence items currently flagged for attention.'}{' '}
              Add or update evidence in the technical-file workspace; it remains
              linked to this same project.
            </p>
            <a
              className="button button-primary"
              href={`${resultsPath}#technical-file`}
              style={{ justifySelf: 'start' }}
            >
              Open technical-file workspace
            </a>
          </div>
        )}

        {step === 'checklist' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              {summary.checklistCompleted} of {summary.checklistTotal} checklist
              items are complete ({checklistPercent}%). There are{' '}
              {summary.unresolvedGapCount} unresolved gaps.
            </p>
            <div
              style={{
                height: 8,
                borderRadius: 999,
                background: 'var(--bg-tertiary)',
              }}
            >
              <div
                style={{
                  width: `${checklistPercent}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: 'var(--accent-primary)',
                }}
              />
            </div>
            <a
              className="button button-primary"
              href={`${resultsPath}#project-tasks`}
              style={{ justifySelf: 'start' }}
            >
              Review checklist and gaps
            </a>
          </div>
        )}

        {step === 'export' && (
          <div style={{ display: 'grid', gap: 16 }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              When the information, evidence, and checklist have been reviewed,
              export the project report for qualified review. CE-Scan does not
              certify conformity.
            </p>
            <a
              className="button button-primary"
              href={resultsPath}
              style={{ justifySelf: 'start' }}
            >
              Open report export
            </a>
            {branding && (
              <div
                style={{
                  display: 'grid',
                  gap: 12,
                  marginTop: 12,
                  paddingTop: 18,
                  borderTop: '1px solid var(--border)',
                }}
              >
                <h3 style={{ margin: 0 }}>Report branding</h3>
                <label style={{ display: 'grid', gap: 6 }}>
                  Attribution mode
                  <select
                    value={branding.mode}
                    disabled={!canConfigureProject}
                    onChange={event =>
                      setBranding({
                        ...branding,
                        mode: event.target.value as ReportBranding['mode'],
                      })
                    }
                  >
                    <option value="ce_scan">CE-Scan</option>
                    <option value="co_branded">Co-branded</option>
                    <option value="white_label">White-label</option>
                  </select>
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  Organization name
                  <input
                    value={branding.organizationName}
                    maxLength={200}
                    disabled={!canConfigureProject}
                    onChange={event =>
                      setBranding({
                        ...branding,
                        organizationName: event.target.value,
                      })
                    }
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  Logo URL
                  <input
                    value={branding.logoUrl}
                    maxLength={2000}
                    disabled={!canConfigureProject}
                    placeholder="https://…/logo.png"
                    onChange={event =>
                      setBranding({ ...branding, logoUrl: event.target.value })
                    }
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  Prepared by
                  <input
                    value={branding.preparedBy}
                    maxLength={300}
                    disabled={!canConfigureProject}
                    onChange={event =>
                      setBranding({
                        ...branding,
                        preparedBy: event.target.value,
                      })
                    }
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  Footer
                  <input
                    value={branding.footerText}
                    maxLength={500}
                    disabled={!canConfigureProject}
                    onChange={event =>
                      setBranding({
                        ...branding,
                        footerText: event.target.value,
                      })
                    }
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  Accent color
                  <input
                    type="color"
                    value={branding.accentColor}
                    disabled={!canConfigureProject}
                    onChange={event =>
                      setBranding({
                        ...branding,
                        accentColor: event.target.value.toUpperCase(),
                      })
                    }
                  />
                </label>
                <label style={{ display: 'grid', gap: 6 }}>
                  Additional disclaimer
                  <textarea
                    value={branding.disclaimer}
                    maxLength={2000}
                    rows={3}
                    disabled={!canConfigureProject}
                    onChange={event =>
                      setBranding({
                        ...branding,
                        disclaimer: event.target.value,
                      })
                    }
                  />
                </label>
                <small style={{ color: 'var(--text-muted)' }}>
                  The mandatory CE-Scan non-certification disclaimer is always
                  retained.
                </small>
                <button
                  className="button button-secondary"
                  type="button"
                  disabled={
                    !canConfigureProject ||
                    isSaving ||
                    !branding.organizationName.trim()
                  }
                  onClick={() => void saveBranding()}
                  style={{ justifySelf: 'start' }}
                >
                  {isSaving ? 'Saving…' : 'Save report branding'}
                </button>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
